import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, BarChart, Bar } from "recharts";
import { LogIn, LogOut, RefreshCw, TrendingUp, FileText, Users } from "lucide-react";
import { KpiNumber, LiveDelta } from "@/components/common/KpiNumber";
import { NOW_SLOT, isInside } from "@/lib/demo/analytics";
import { checkpointName, companyName } from "@/lib/demo/seed";
import { fmt, fmtDec, fullName, byRecent } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

export const Panel = ({ children, className = "", testId }) => <div className={`rounded-[28px] border border-slate-200 bg-white p-6 sm:p-7 ${className}`} data-testid={testId}>{children}</div>;
export const Eyebrow = ({ children, className = "" }) => { const { t } = useI18n(); return <p className={`text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 ${className}`}>{typeof children === "string" ? t(children) : children}</p>; };
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }, formatter: (v) => fmt(v) };

export const KpiRow = ({ data }) => {
  const { t } = useI18n();
  const items = [
    ["Registrados", data.kpis.registrados, "registrados"],
    ["Asistentes únicos", data.kpis.unique, "unique"],
    ["Ahora en el recinto", data.kpis.inside, "inside", true],
    ["Empresas", data.kpis.empresas, "empresas"],
    ["CV compartidos", data.kpis.cv, "cv"],
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {items.map(([label, value, id, hl]) => (
        <div key={id} className={`flex min-h-[150px] flex-col justify-between rounded-[24px] border p-6 ${hl ? "border-upv bg-upv text-white" : "border-slate-200 bg-white"}`} data-testid={`dash-kpi-card-${id}`}>
          <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${hl ? "text-white/70" : "text-slate-400"}`}>{t(label)}</p>
          <div>
            <KpiNumber value={value} testId={`dash-kpi-${id}`} className="font-display text-4xl font-bold tracking-tight xl:text-5xl" />
            <div className="mt-2 h-5">
              {hl && data.view === "day1" ? <span className="text-xs text-white/70">{t("Jornada finalizada")}</span> : <span className={hl ? "[&>span]:bg-white/15 [&>span]:text-white" : ""}><LiveDelta value={data.deltas[id]} testId={`dash-kpi-delta-${id}`} /></span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const OccupancyChart = ({ data }) => {
  const { t } = useI18n();
  return (
  <Panel className="lg:col-span-8" testId="dash-occupancy-chart">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><Eyebrow>Afluencia</Eyebrow><h3 className="mt-1 font-display text-2xl font-bold tracking-tight">{t("Afluencia durante el día")}</h3></div>
      <div className="flex gap-4 text-xs font-semibold text-slate-500">
        {data.series.includes("d1") && <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-slate-400" />{t("Día 1")}</span>}
        {data.series.includes("d2") && <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-upv" />{t("Día 2")}</span>}
      </div>
    </div>
    <div className="mt-6 h-[320px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data.occupancy} margin={{ left: -10, right: 10 }}>
          <defs>
            <linearGradient id="gD2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B0000" stopOpacity={0.28} /><stop offset="100%" stopColor="#8B0000" stopOpacity={0} /></linearGradient>
            <linearGradient id="gD1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#94A3B8" stopOpacity={0.25} /><stop offset="100%" stopColor="#94A3B8" stopOpacity={0} /></linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="#F1F5F9" />
          <XAxis dataKey="time" tickLine={false} axisLine={false} fontSize={11} stroke="#94A3B8" interval={1} />
          <YAxis tickLine={false} axisLine={false} fontSize={11} stroke="#94A3B8" tickFormatter={fmt} />
          <Tooltip {...tip} />
          {data.series.includes("d1") && <Area type="monotone" dataKey="d1" name={t("Día 1")} stroke="#94A3B8" strokeWidth={2} fill="url(#gD1)" />}
          {data.series.includes("d2") && <Area type="monotone" dataKey="d2" name={t("Día 2")} stroke="#8B0000" strokeWidth={2.5} fill="url(#gD2)" connectNulls={false} />}
          {data.view !== "day1" && <ReferenceLine x={NOW_SLOT} stroke="#0F172A" strokeDasharray="4 4" label={{ value: t("Ahora"), position: "insideTopRight", fontSize: 11, fill: "#0F172A" }} />}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </Panel>
  );
};

export const FlowCards = ({ data }) => {
  const { t } = useI18n();
  return (
  <div className="flex flex-col gap-4 lg:col-span-4">
    <div className="rounded-[28px] bg-slate-950 p-7 text-white" data-testid="dash-peak-card">
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/50"><TrendingUp className="h-3.5 w-3.5" /> {t("Pico de ocupación")}</p>
      <p className="mt-3 font-display text-5xl font-bold tracking-tight" data-testid="dash-peak-value">{fmt(data.peak.value)} <span className="text-xl font-medium text-white/60">{t("personas")}</span></p>
      <p className="mt-1 font-mono text-lg font-bold text-rose-300" data-testid="dash-peak-time">{data.peak.label ? `${t(data.peak.label)} · ` : ""}{data.peak.time}</p>
    </div>
    {[["Entradas", data.flows.entries, "entries", LogIn], ["Salidas", data.flows.exits, "exits", LogOut], ["Reentradas", data.flows.reentries, "reentries", RefreshCw]].map(([l, v, id, Icon]) => (
      <div key={id} className="flex items-center justify-between rounded-[22px] border border-slate-200 bg-white px-6 py-4" data-testid={`dash-flow-${id}`}>
        <span className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-500"><Icon className="h-4 w-4 text-upv" />{t(l)}</span>
        <span className="flex items-center gap-2"><LiveDelta value={data.deltas[id]} /><KpiNumber value={v} testId={`dash-flow-${id}-value`} className="font-display text-3xl font-bold" /></span>
      </div>
    ))}
  </div>
  );
};

export const CompanyInteraction = ({ data }) => {
  const { t } = useI18n();
  const max = data.ranking[0]?.value || 1;
  return (
    <Panel className="lg:col-span-8" testId="dash-company-interaction">
      <Eyebrow>Interacción con empresas</Eyebrow>
      <div className="mt-5 grid grid-cols-2 gap-4 xl:grid-cols-4">
        {[["CV compartidos", data.interaction.cv, fmt, "cv"], ["Participantes que compartieron CV", data.interaction.cvParticipants, fmt, "cv-participants"], ["Media por participante", data.interaction.avg, (v) => fmtDec(v), "avg"], ["Empresas visitadas por participante", data.interaction.companiesVisited, (v) => fmtDec(v), "visited"]].map(([l, v, f, id]) => (
          <div key={id} className="rounded-2xl bg-slate-50 p-5">
            <KpiNumber value={v} format={f} testId={`dash-interaction-${id}`} className="font-display text-3xl font-bold tracking-tight" />
            <p className="mt-2 text-xs font-medium leading-snug text-slate-500">{t(l)}</p>
          </div>
        ))}
      </div>
      <h4 className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">{t("Empresas con más interacciones")}</h4>
      <ol className="mt-4 space-y-3">
        {data.ranking.slice(0, 5).map((c, i) => (
          <li key={c.id} className="grid grid-cols-[28px_150px_1fr_70px] items-center gap-3 sm:grid-cols-[28px_180px_1fr_80px]" data-testid={`dash-ranking-${c.id}`}>
            <span className="font-mono text-sm font-bold text-upv">{i + 1}</span>
            <span className="truncate text-sm font-semibold">{c.name}</span>
            <span className="h-2.5 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full bg-upv transition-[width] duration-700" style={{ width: `${(c.value / max) * 100}%` }} /></span>
            <KpiNumber value={c.value} testId={`dash-ranking-${c.id}-value`} className="text-right font-mono text-sm font-bold" />
          </li>
        ))}
      </ol>
    </Panel>
  );
};

export const LiveFeed = ({ state, participant, participantsById }) => {
  const { t } = useI18n();
  const items = [
    ...state.accessEvents.map((e) => ({ ...e, kind: e.type, sub: t(checkpointName(e.checkpointId)) })),
    ...state.interactions.map((i) => ({ ...i, kind: "cv", sub: t("CV compartido con {company}", { company: companyName(i.companyId) }) })),
  ].filter((x) => x.day === 2).sort(byRecent).slice(0, 7);
  const inside = isInside(state, participant.id);
  return (
    <Panel className="lg:col-span-4" testId="dash-live-feed">
      <div className="flex items-center justify-between"><Eyebrow>Actividad en directo</Eyebrow><span className="h-2 w-2 animate-pulse rounded-full bg-upv" /></div>
      <div className={`mt-4 rounded-2xl p-4 ${inside ? "bg-emerald-50" : "bg-slate-50"}`} data-testid="dash-participant-status">
        <p className="text-xs text-slate-500">{t("Participante de la demo")}</p>
        <p className="font-semibold">{fullName(participant)}</p>
        <p className={`mt-1 text-xs font-bold tracking-wider ${inside ? "text-emerald-700" : "text-slate-500"}`} data-testid="dash-participant-status-label">{t(inside ? "● DENTRO DEL RECINTO" : "○ FUERA DEL RECINTO")}</p>
      </div>
      <ul className="mt-4 space-y-1">
        {items.map((x) => {
          const p = participantsById[x.participantId];
          const Icon = x.kind === "cv" ? FileText : x.kind === "entry" ? LogIn : LogOut;
          return (
            <li key={x.id} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${x.id.startsWith("live-") ? "bg-upv-light/70" : ""}`} data-testid={`dash-feed-item-${x.id}`}>
              <span className="font-mono text-xs font-bold text-slate-500">{x.time}</span>
              <Icon className={`h-3.5 w-3.5 shrink-0 ${x.kind === "cv" ? "text-upv" : "text-slate-500"}`} />
              <span className="min-w-0"><span className="block truncate text-sm font-semibold">{p ? fullName(p) : ""}</span><span className="block truncate text-xs text-slate-500">{x.kind === "cv" ? x.sub : `${t(x.kind === "entry" ? "Entrada" : "Salida")} · ${x.sub}`}</span></span>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
};

const HBars = ({ data, color }) => {
  const { t } = useI18n();
  return (
  <div className="mt-4 h-[300px]">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data.map((d) => ({ ...d, name: t(d.name) }))} layout="vertical" margin={{ left: 0, right: 20 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={160} tickLine={false} axisLine={false} fontSize={12} stroke="#475569" />
        <Tooltip {...tip} cursor={{ fill: "#F8FAFC" }} />
        <Bar dataKey="value" name="Total" fill={color} radius={[0, 6, 6, 0]} barSize={20} />
      </BarChart>
    </ResponsiveContainer>
  </div>
  );
};

export const AcademicCharts = ({ data }) => {
  const { t } = useI18n();
  return (
  <div className="grid gap-6 lg:grid-cols-2">
    <Panel testId="dash-chart-centros"><Eyebrow className="flex items-center gap-2"><Users className="h-3.5 w-3.5" /> {t("Analytics académicos")}</Eyebrow><h3 className="mt-1 font-display text-2xl font-bold">{t("Participantes por centro")}</h3><HBars data={data.centros} color="#0F172A" /></Panel>
    <Panel testId="dash-chart-titulaciones"><Eyebrow>Analytics académicos</Eyebrow><h3 className="mt-1 font-display text-2xl font-bold">{t("Titulaciones con más interacciones")}</h3><HBars data={data.titulaciones} color="#8B0000" /></Panel>
  </div>
  );
};
